import * as React from 'react';
import { useState } from 'react';
import { Button, Empty, List, Modal, Progress, Spin, message, Popconfirm, Row, Col } from 'antd';
import { DeleteOutlined, SyncOutlined } from '@ant-design/icons';
import { ValidatedTree } from './Popup/MapPopup';
import { getValidatedTreesAsync, removeValidatedTreeAsync } from '../../Service/TreeService';
import * as TreeRepository from "../../Repository/TreeRepository";
import { entriesToFormData } from '../../Helper/DataHelper';
import SyncQueueError from '../FormPage/SyncQueue/SyncQueueError';
import {
  NO_NETWORK_CONNECTIVITY,
  VALIDATION_REMOVED_FROM_QUEUE,
  treeSynced,
  treeSyncFailed,
} from '../../Helper/BannerMessages';

interface ValidatedTreeData extends ValidatedTree {
  state: 'pending' | 'syncing';
}

interface ValidatedTreesProps {
  getTrees: (params?: any, filter?: any, pager?: any, errorCallback?: Function) => Promise<any>;
  /** Set while a validation is being written to the queue: the count gives way to a spinner. */
  saving?: boolean;
}

/**
 * Body of the validation request.
 *
 * The queued photo comes back from IndexedDB as a nameless Blob, and WebKit cannot
 * stream such a Blob in a FormData: the part goes out broken and the API answers 500,
 * while Android accepts the very same call - which is why a validation used to be
 * lost on iOS only. `entriesToFormData` is the helper the tree queue already uses: it
 * re-wraps the Blob into a fresh File, names it from its mime type, and the field is
 * uploaded as `image`, the name the API expects.
 */
function buildValidationFormData(validationData: any) {
  const entries = Object.entries(validationData)
    .map(([key, value]) => (key === 'photo' ? ['image', value] : [key, value]));

  return entriesToFormData(entries);
}

export default function ValidatedTrees(props: ValidatedTreesProps) {
  const { getTrees, saving } = props;
  const [trees, setTrees] = useState<ValidatedTreeData[]>([]);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncProgress, setSyncProgress] = useState<number>(0);
  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const [hasFetchError, setHasFetchError] = useState<boolean>(false);
  const [syncSuccess, setSyncSuccess] = useState<boolean>(false);
  const [syncedCount, setSyncedCount] = useState<number>(0);



  const fetchTrees = async () => {
    const storedTrees = await getValidatedTreesAsync();
    setTrees(storedTrees.map(t => ({ ...t, state: 'pending' as const })));
  };

  React.useEffect(() => {
    fetchTrees();
    const interval = setInterval(fetchTrees, 3000); // UI Refresh every 3 seconds
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    if (popupVisible) {
      fetchTrees();
      setSyncSuccess(false);
    }
  }, [popupVisible]);

  const totalTreesCount = trees.reduce((acc, tree) => acc + (parseInt(tree.number) || 0), 0);

  const handleDelete = async (treeId: string) => {
    await removeValidatedTreeAsync(treeId);
    message.success(VALIDATION_REMOVED_FROM_QUEUE);
    await fetchTrees();
  };

  const handleSyncOne = async (tree: ValidatedTreeData) => {
    setHasFetchError(false);
    if (!navigator.onLine) {
      setHasFetchError(true);
      message.warning(NO_NETWORK_CONNECTIVITY)
      return;
    }
    setTrees(prev => prev.map(t => t.id === tree.id ? { ...t, state: 'syncing' } : t));
    try {
      if (tree.validationData && Object.keys(tree.validationData).length > 0) {
        const formData = buildValidationFormData(tree.validationData);

        await TreeRepository.validateTree(tree.id, {
          httpConfig: {
            method: 'POST',
            body: formData,
            multipart: true,
          }
        });
      } else {
        // No data to report: a validation with an empty body, which is what the API keeps today
        await TreeRepository.validateTree(tree.id, {
          httpConfig: {
            method: 'POST'
          }
        });
      }

      await removeValidatedTreeAsync(tree.id);
      message.success(treeSynced(tree.id));
      setSyncedCount(parseInt(tree.number) || 0);
      setSyncSuccess(true);
      await fetchTrees();
      getTrees();
    } catch (err) {
      console.error(err);
      message.error(treeSyncFailed(tree.id));
      setHasFetchError(true);
      setTrees(prev => prev.map(t => t.id === tree.id ? { ...t, state: 'pending' } : t));
    }
  };

  const handleSyncAll = async () => {
    setHasFetchError(false);
    if (!navigator.onLine) {
      setHasFetchError(true);
      message.warning(NO_NETWORK_CONNECTIVITY)
      return;
    }
    setSyncing(true);
    setSyncProgress(0);
    const total = trees.length;
    let successCount = 0;
    let failed = false;

    for (let i = 0; i < trees.length; i++) {
      const tree = trees[i];
      try {
        if (tree.validationData && Object.keys(tree.validationData).length > 0) {
          const formData = buildValidationFormData(tree.validationData);

          await TreeRepository.validateTree(tree.id, {
            httpConfig: {
              method: 'POST',
              body: formData,
              multipart: true,
            }
          });
        } else {
          // Same call as above without data, see the single-tree path
          await TreeRepository.validateTree(tree.id, {
            httpConfig: {
              method: 'POST'
            }
          });
        }

        await removeValidatedTreeAsync(tree.id);
        successCount += (parseInt(tree.number) || 0);

        // Update UI in real-time
        await fetchTrees();
        getTrees();
      } catch (err) {
        console.error(`Failed to sync tree ${tree.id}`, err);
        setHasFetchError(true);
        failed = true;
        break;
      }
      setSyncProgress(Math.round(((i + 1) / total) * 100));
    }

    // The success screen must not be shown after a failure: the validations sent
    // before it are stored, the failing one is kept in the queue and the user has to
    // know, instead of reading "N trees synced successfully".
    setSyncedCount(successCount);
    setSyncSuccess(!failed);
    setSyncing(false);
  };



  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {/* The class is what the tree window looks up to aim its closing collapse at (see MapPopup):
            a validation that has just been saved collapses towards this button. The spinner takes the
            place of the count for as long as the validation is being written to the queue. */}
        <Button
          type='link'
          className={'btn-open-validated-queue'}
          onClick={() => setPopupVisible(!popupVisible)}
        >
          show queue ({saving ? <Spin size='small' /> : totalTreesCount})
        </Button>
      </div>
      <Modal
        open={popupVisible}
        onOk={() => { setPopupVisible(false) }}
        okText={'Close'}
        onCancel={() => !syncing && setPopupVisible(false)}
        closable={!syncing}
        maskClosable={!syncing}
        cancelButtonProps={{ style: { display: "none" } }}
        title={'Trees in queue'}
        width={600}
      >
        {syncSuccess ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <img
              src="/dist/icons/success_emoticon.svg"
              alt="Success"
              style={{ width: '100px', marginBottom: '20px' }}
            />
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {syncedCount} tree{ syncedCount > 1 ? 's' : '' }<br />synced successfully
            </div>
          </div>
        ) : (
          <>
            {trees.length > 0 && (
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <SyncQueueError
                  syncCallback={handleSyncAll}
                  syncing={syncing}
                  hasFetchError={hasFetchError}
                />
                {syncing && (
                  <div style={{ marginTop: '10px' }}>
                    <Row justify="center" style={{ marginBottom: 16 }}>
                      <Col span={24} style={{ textAlign: 'center' }}>
                        <p>Please wait while trees are being submitted</p>
                      </Col>
                    </Row>
                    <Row justify="center">
                      <Col span={24}>
                        <Progress percent={syncProgress} showInfo={false} status="active" />
                      </Col>
                    </Row>
                    <Row justify="center" style={{ marginTop: 8 }}>
                      <Col span={24} style={{ textAlign: 'center' }}>
                        {Math.round((syncProgress / 100) * trees.length)} / {trees.length}
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            )}
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {trees.length === 0
                ? <Empty description={'No data'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                : <List
                  itemLayout="horizontal"
                  dataSource={trees}
                  renderItem={(tree) => {
                    const isItemSyncing = tree.state === 'syncing' || syncing;
                    return (
                      <List.Item
                        key={tree.id}
                        actions={[
                          <Button
                            key="sync"
                            type="link"
                            icon={<SyncOutlined />}
                            onClick={() => handleSyncOne(tree)}
                            disabled={isItemSyncing}
                            loading={tree.state === 'syncing'}
                          >
                            sync
                          </Button>,
                          <Popconfirm
                            key="delete"
                            title="Delete this item?"
                            onConfirm={() => handleDelete(tree.id)}
                            okText="Yes"
                            cancelText="No"
                            disabled={isItemSyncing}
                          >
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              disabled={isItemSyncing}
                            >
                              delete
                            </Button>
                          </Popconfirm>
                        ]}
                      >
                        <List.Item.Meta
                          title={`#${tree.id} - ${tree.number}x ${tree.type}`}
                          description={tree.state === 'syncing' ? 'Syncing...' : 'Pending sync'}
                        />
                      </List.Item>
                    )
                  }}
                />
              }
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}